import { Spin } from 'antd';
import { useLayoutEffect, useRef, useState } from 'react';

export interface BaseLoadingProps {
  label: string;
  fullScreen?: boolean;
  overlay?: boolean;
  centerInVisibleArea?: boolean;
  mask?: boolean;
}

/** 提供可访问的居中加载状态，全屏启动和内容区加载共用。 */
export default function BaseLoading({
  label,
  fullScreen = false,
  overlay = false,
  centerInVisibleArea = false,
  mask = true,
}: BaseLoadingProps) {
  const container = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const visibleCenter = overlay && centerInVisibleArea;

  useLayoutEffect(() => {
    const element = container.current;
    if (!visibleCenter || !element) return;
    let frame = 0;
    const ancestors: HTMLElement[] = [];
    for (
      let parent = element.parentElement;
      parent;
      parent = parent.parentElement
    ) {
      ancestors.push(parent);
    }
    const update = () => {
      const rect = element.getBoundingClientRect();
      let left = Math.max(0, rect.left);
      let right = Math.min(window.innerWidth, rect.right);
      let top = Math.max(0, rect.top);
      let bottom = Math.min(window.innerHeight, rect.bottom);
      for (const parent of ancestors) {
        const style = getComputedStyle(parent);
        const bounds = parent.getBoundingClientRect();
        if (/(auto|scroll|hidden|clip)/.test(style.overflowX)) {
          left = Math.max(left, bounds.left + parent.clientLeft);
          right = Math.min(
            right,
            bounds.left + parent.clientLeft + parent.clientWidth,
          );
        }
        if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) {
          top = Math.max(top, bounds.top + parent.clientTop);
          bottom = Math.min(
            bottom,
            bounds.top + parent.clientTop + parent.clientHeight,
          );
        }
      }
      const next = {
        left: (left + Math.max(left, right)) / 2 - rect.left,
        top: (top + Math.max(top, bottom)) / 2 - rect.top,
      };
      setPosition((current) =>
        current.left === next.left && current.top === next.top ? current : next,
      );
    };
    // 滚动与尺寸变化合并到一帧，不为长表格持续测量每个单元格。
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const observer = new ResizeObserver(schedule);
    [element, ...ancestors].forEach((node) => observer.observe(node));
    window.addEventListener('resize', schedule);
    document.addEventListener('scroll', schedule, true);
    update();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      document.removeEventListener('scroll', schedule, true);
    };
  }, [visibleCenter]);

  return (
    <div
      ref={container}
      className={`base-loading grid place-items-center min-h-full text-primary${
        fullScreen ? ' base-loading-fullscreen fixed inset-0 bg-layout' : ''
      }${
        overlay
          ? ` base-loading-overlay absolute inset-0 z-[5]${
              mask ? ' bg-surface-soft' : ''
            }`
          : ''
      }`}
      role="status"
      aria-label={label}
    >
      {visibleCenter ? (
        <span
          className="base-loading-indicator absolute flex -translate-x-1/2 -translate-y-1/2 leading-none"
          style={position}
        >
          <Spin size="large" />
        </span>
      ) : (
        <Spin size="large" />
      )}
    </div>
  );
}
